import React, { useMemo, useState, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { BarChart3, ArrowUpRight, ArrowDownRight, X } from "lucide-react";

/**
 * AttendanceDistributionChart
 * Displays a professional vertical bar chart for attendance status distribution.
 * Computes real percentages from attendance records (same data source as EmployeeTable_Final).
 */
const AttendanceDistributionChart = ({
  attendanceData = [],
  previousMonthData = [],
  theme = "light",
  language = "en",
}) => {
  const isRTL = language === "ar";
  const isDarkMode = theme === "dark";
  const [openCategory, setOpenCategory] = useState(null);
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });

  useLayoutEffect(() => {
    const update = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // ── Normalize status ───────────────────────────────────────────────────
  const normalizeStatus = (status) => {
    if (!status) return null;
    const s = String(status).toLowerCase().trim();
    // Order matters: check compound / specific buckets before the generic
    // present/absent/late substrings, otherwise "HalfDay(morning) - Late" and
    // "HalfDay(morning) - Absent" get miscounted as late/absent.
    if (s.includes("rest") || s.includes("weekend")) return "rest_day";
    if (s.includes("holiday")) return "on_leave"; // backend emits holidays as "On Leave"
    if (s.includes("half")) return "half_day";
    if (s.includes("leave") || s.includes("onleave")) return "on_leave";
    if (s.includes("present") || s === "p") return "present";
    if (s.includes("late") || s === "l") return "late";
    if (s.includes("absent") || s === "a") return "absent";
    return s;
  };

  // ── Calculate distributions ────────────────────────────────────────────
  const chartData = useMemo(() => {
    const total = attendanceData.length;
    if (total === 0) return { categories: [], total: 0 };

    const recordsFor = (status) => attendanceData.filter((r) => normalizeStatus(r.status) === status);
    const countStatus = (data, status) =>
      data.filter((r) => normalizeStatus(r.status) === status).length;

    const prevTotal = previousMonthData.length;

    const buildCategory = (key, label, icon, gradient, glow, status, prevCount) => {
      const records = recordsFor(status);
      const currentCount = records.length;
      const pct = total > 0 ? Math.round((currentCount / total) * 100) : 0;
      const prevPct = prevTotal > 0 ? Math.round((prevCount / prevTotal) * 100) : 0;
      const change = prevTotal > 0 ? pct - prevPct : null;
      return {
        key,
        label,
        icon,
        gradient,
        glow,
        count: currentCount,
        percentage: pct,
        change,
        records,
      };
    };

    const categories = [
      buildCategory(
        "present",
        language === "ar" ? "حاضرون" : "Present",
        "",
        ["#10b981", "#059669"],
        "rgba(16, 185, 129, 0.25)",
        "present",
        countStatus(previousMonthData, "present")
      ),
      buildCategory(
        "absent",
        language === "ar" ? "غائبون" : "Absent",
        "",
        ["#ef4444", "#dc2626"],
        "rgba(239, 68, 68, 0.25)",
        "absent",
        countStatus(previousMonthData, "absent")
      ),
      buildCategory(
        "late",
        language === "ar" ? "متأخرون" : "Late",
        "",
        ["#f59e0b", "#d97706"],
        "rgba(245, 158, 11, 0.25)",
        "late",
        countStatus(previousMonthData, "late")
      ),
      buildCategory(
        "onLeave",
        language === "ar" ? "في إجازة" : "On Leave",
        "",
        ["#3b82f6", "#2563eb"],
        "rgba(59, 130, 246, 0.25)",
        "on_leave",
        countStatus(previousMonthData, "on_leave")
      ),
      buildCategory(
        "halfDay",
        language === "ar" ? "نصف يوم" : "Half-Day",
        "",
        ["#8b5cf6", "#7c3aed"],
        "rgba(139, 92, 246, 0.25)",
        "half_day",
        countStatus(previousMonthData, "half_day")
      ),
      buildCategory(
        "restDay",
        language === "ar" ? "راحة" : "Rest Day",
        "",
        ["#64748b", "#475569"],
        "rgba(100, 116, 139, 0.25)",
        "rest_day",
        countStatus(previousMonthData, "rest_day")
      ),
    ];

    return { categories, total };
  }, [attendanceData, previousMonthData, language]);

  const maxCount = Math.max(...chartData.categories.map((c) => c.count), 1);
  const BAR_HEIGHT = 160;
  const isCompact = viewport.w <= 480;

  return (
    <div
      style={{
        background: isDarkMode ? "rgba(58, 58, 58, 0.02)" : "rgba(255,255,255,0.9)",
        borderRadius: "16px",
        border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.06)",
        boxShadow: isDarkMode
          ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)"
          : "0 8px 32px rgba(0,0,0,0.08)",
        padding: isCompact ? "16px" : "24px",
        WebkitBackdropFilter: isDarkMode ? "blur(20px) saturate(160%)" : "blur(8px)",
        display: "flex",
        flexDirection: "column",
        direction: isRTL ? "rtl" : "ltr",
        position: "relative",
        minWidth: 0,
      }}
    >

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: "16px",
          borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
          flexDirection: isRTL ? "row" : "row",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexDirection: "row",
          }}
        >
          <BarChart3 size={24} color={isDarkMode ? "rgba(255,255,255,0.7)" : "#555"} />
          <h3
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: isDarkMode ? "#f1f5f9" : "#1e293b",
              margin: 0,
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {language === "ar" ? "توزيع الحضور" : "Attendance Distribution"}
          </h3>
        </div>

        {/* Total badge */}
        <div
          style={{

            padding: "6px 14px",
            borderRadius: "20px",
            background: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
            border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
          }}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
              fontFamily: "'Cairo', sans-serif",

            }}
          >
            {language === "ar" ? "الإجمالي: " : "Total: "}
          </span>
          <span
            style={{
              fontSize: "14px",
              fontWeight: 700,

              color: isDarkMode ? "#f1f5f9" : "#1e293b",
              fontFamily: "'Cairo', sans-serif",

            }}
          >
            {chartData.total}
          </span>
        </div>
      </div>

      {/* ── Bars + Labels (single unified grid) ────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: isCompact ? "space-between" : "flex-start",
          gap: isCompact ? "6px" : chartData.categories.length <= 3 ? "40px" : "24px",
          padding: isCompact ? "0 4px" : "0 20px",
          marginTop: "140px",
          position: "relative",
          zIndex: 1,
          flexDirection: isRTL ? "row-reverse" : "row",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          minWidth: 0,
        }}
      >
        {chartData.categories.map((cat, idx) => {
          const barH = maxCount > 0 ? (cat.count / maxCount) * BAR_HEIGHT : 0;
          const clickable = cat.count > 0;

          return (
            <div
              key={cat.key}
              onClick={clickable ? () => setOpenCategory(cat.key) : undefined}
              title={clickable ? (isRTL ? "اضغط لعرض الأسماء" : "Click to see names") : undefined}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
                maxWidth: isCompact ? "56px" : "90px",
                minWidth: isCompact ? "40px" : "54px",
                cursor: clickable ? "pointer" : "default",
              }}
            >


              {/* ── Bar ── */}
              <div
                style={{
                  width: "100%",
                  height: `${BAR_HEIGHT + 10}px`,
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: `${Math.max(barH, 6)}px`,
                    background: cat.gradient[0],
                    borderRadius: "6px 6px 0 0",
                    transition: "height 0.3s ease",
                  }}
                />
              </div>

              {/* ── Label ── */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "4px",
                  paddingTop: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "2px",
                      background: cat.gradient[0],
                      flexShrink: 0,
                    }}
                  />
                  <div
                    style={{
                      fontSize: isCompact ? "11px" : "12px",
                      fontWeight: 600,
                      color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                      fontFamily: "'Cairo', sans-serif",
                      whiteSpace: isCompact ? "normal" : "nowrap",
                      textAlign: "center",
                    }}
                  >
                    {cat.label}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {cat.count}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Monthly Comparison Cards ──────────────────────────────────── */}
      {/* {previousMonthData.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.min(chartData.categories.length, 4)}, 1fr)`,
            gap: "10px",
            marginTop: "20px",
            paddingTop: "20px",
            borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
          }}
        >
          {chartData.categories.map((cat) => (
            <div
              key={cat.key}
              style={{
                padding: "10px 12px",
                borderRadius: "12px",
                background: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"}`,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: isDarkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                  fontFamily: "'Cairo', sans-serif",
                  marginBottom: "4px",
                }}
              >
                {language === "ar" ? "مقارنة بالشهر السابق" : "vs Last Month"}
              </div>
              {cat.change !== null && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                >
                  {cat.change > 0 ? (
                    <ArrowUpRight size={14} color={cat.key === "absent" || cat.key === "late" ? "#ef4444" : "#10b981"} />
                  ) : cat.change < 0 ? (
                    <ArrowDownRight size={14} color={cat.key === "absent" || cat.key === "late" ? "#10b981" : "#ef4444"} />
                  ) : null}
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: cat.change === 0
                        ? (isDarkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)")
                        : (cat.change > 0
                          ? (cat.key === "absent" || cat.key === "late" ? "#ef4444" : "#10b981")
                          : (cat.key === "absent" || cat.key === "late" ? "#10b981" : "#ef4444")),
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    {cat.change === 0
                      ? (language === "ar" ? "بدون تغيير" : "No change")
                      : `${cat.change > 0 ? "+" : ""}${cat.change}%`}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )} */}

      {openCategory && (() => {
        const cat = chartData.categories.find((c) => c.key === openCategory);
        if (!cat) return null;
        return createPortal(
          <div
            onClick={() => setOpenCategory(null)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: `${viewport.w}px`,
              height: `${viewport.h}px`,
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(2px)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              /* Isolate this overlay from any ancestor overflow/transform */
              contain: "strict",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: isDarkMode ? "#1e293b" : "#fff",
                borderRadius: 14,
                padding: 20,
                width: `${Math.min(420, viewport.w * 0.9)}px`,
                maxHeight: `${viewport.h * 0.7}px`,
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
                boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
                direction: isRTL ? "rtl" : "ltr",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <h3
                  style={{
                    margin: 0, fontSize: 16, fontWeight: 700,
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: cat.gradient[0], display: "inline-block" }} />
                  {cat.label}
                  <span style={{ fontWeight: 400, fontSize: 13, color: isDarkMode ? "rgba(255,255,255,0.55)" : "#64748b" }}>
                    ({cat.count})
                  </span>
                </h3>
                <button
                  onClick={() => setOpenCategory(null)}
                  style={{ background: "transparent", border: "none", cursor: "pointer", color: isDarkMode ? "rgba(255,255,255,0.55)" : "#64748b", padding: 4, display: "flex" }}
                >
                  <X size={18} />
                </button>
              </div>

              <div>
                {cat.records.map((r, i) => (
                  <div
                    key={r.id ?? `${r.employeeName}-${i}`}
                    style={{
                      display: "flex", justifyContent: "space-between", gap: 12,
                      padding: "8px 0",
                      borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: isDarkMode ? "#f1f5f9" : "#1e293b" }}>
                      {r.employeeName || r.employeeId || "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default AttendanceDistributionChart;
